import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';

import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import { StyledButtonLink } from '../Button';
import NoValue from '../NoValue/NoValue';

import { formatPhone } from './utils';

export interface PhoneDisplayProps extends BaseProps, NoChildrenProp {
  /** Phone number, no 'tel:' scheme, '+18887342669' */
  value?: string;
  /** The text to be displayed. */
  displayText?: string;
  /**
   * Controls styling of the field.
   * @default 'link'
   */
  variant?: 'link' | 'text';
  /** Options to define formatting. */
  formattingOptions?: {
    /**
     * Display phone number with country calling code
     * @default true
     */
    showCountryCode?: boolean;
  };
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLAnchorElement | HTMLSpanElement>;
}

const PhoneDisplay: ForwardRefForwardPropsComponent<PhoneDisplayProps> = forwardRef(
  function PhoneDisplay(
    {
      value,
      displayText,
      variant = 'link',
      formattingOptions: { showCountryCode = true } = {},
      ...restProps
    }: PropsWithoutRef<PhoneDisplayProps>,
    ref: PhoneDisplayProps['ref']
  ) {
    const displayValue = value ? (
      displayText || formatPhone(value || '', { nationalFormat: !showCountryCode }) || value
    ) : (
      <NoValue />
    );

    return variant === 'text' || !value ? (
      <span ref={ref as Ref<HTMLSpanElement>} {...restProps}>
        {displayValue}
      </span>
    ) : (
      <StyledButtonLink
        {...restProps}
        variant='link'
        ref={ref as Ref<HTMLAnchorElement>}
        href={`tel:${value}`}
      >
        {displayValue}
      </StyledButtonLink>
    );
  }
);

export default PhoneDisplay;
